import React from 'react'
import {
  Box,
  Button,
  Container,
  FormHelperText,
  Input,
  InputLabel,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import useLeadActions, {
  allowedUploadExtensions,
} from '../../../hooks/useLeadActions'
import { WidgetTRefs } from '../../../types/widget'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { ResumeUploadButtonCallback } from '../../Buttons/ResumeUpload'
import {
  useMixpanelCTAClickTracking,
  useMpEventType,
} from '../../../utils/mixpanel'
import { MixpanelCTALinkActions, MixpanelEvents } from '../../../types/mixpanel'

type Props = {
  onChange:
    | React.ChangeEventHandler<HTMLInputElement>
    | ResumeUploadButtonCallback
  resumeApiStatus: 'idle' | 'submitting' | 'error' | 'success'
}

export default function MobileUploadWidget({
  onChange,
  resumeApiStatus,
}: Props) {
  const { classes, cx } = useStyles()
  const { errors } = useLeadActions()
  const { t } = useTranslation()
  const { defaultResumeProduct } = useSiteMetadata()
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()
  const mpEventType = useMpEventType()

  return (
    <Box component="form" className={classes.root}>
      <Box className={classes.contentWrapper}>
        <Container className={classes.container}>
          <InputLabel
            htmlFor="file-upload"
            className={cx(
              'custom-file-upload',
              classes.label,
              errors.resumeFile && classes.bottomBorder
            )}
          >
            <span>
              {!(resumeApiStatus === 'submitting') &&
                t(`${WidgetTRefs.MobileTRef}.inputPlaceholder`, {
                  defaultResumeProduct,
                })}
            </span>
            <span>+</span>
          </InputLabel>
          <Input
            id="file-upload"
            type="file"
            className={classes.input}
            // @ts-ignore
            onChange={onChange}
            inputProps={{
              accept: allowedUploadExtensions.join(', '),
            }}
            disabled={resumeApiStatus === 'submitting'}
          />
        </Container>

        {errors.resumeFile && (
          <FormHelperText component="p" error className={classes.error}>
            {t(`${WidgetTRefs.CommonTRef}.formatError`)}
          </FormHelperText>
        )}
        <Button
          variant="contained"
          component="label"
          className={classes.uploadButton}
          disabled={resumeApiStatus === 'submitting'}
          onClick={(e) =>
            handleMpCTAClickTracking(
              e.currentTarget.innerText,
              MixpanelCTALinkActions.ResumeUploadAction,
              {
                eventName: MixpanelEvents.MobileGetMyReviewButton,
                trackingData: {
                  event_type: mpEventType,
                },
                trackOn: null,
              }
            )
          }
        >
          <Input
            type="file"
            // @ts-ignore
            onChange={onChange}
            className={classes.input}
            inputProps={{
              accept: allowedUploadExtensions.join(', '),
            }}
          />
          {resumeApiStatus === 'submitting'
            ? t(`${WidgetTRefs.StatusModalTRef}.buttonState.loadingText`, {
                defaultResumeProduct,
              })
            : t(`${WidgetTRefs.StatusModalTRef}.buttonState.defaultText`, {
                defaultResumeProduct,
              })}
        </Button>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
  },
  contentWrapper: {
    padding: '1rem',
    background: theme.palette.background.paper,
    borderRadius: '6px',
    margin: '0 auto',
    width: '100%',
    maxWidth: 350,
  },
  container: {
    padding: 0,
  },
  uploadButton: {
    border: 'none',
    borderRadius: '0 0 3px 3px',
    width: '100%',
    '&.Mui-disabled': {
      '&&': {
        background: theme.colors.blue[200],
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        opacity: 1,
      },
    },
  },
  label: {
    display: 'flex',
    backgroundColor: theme.palette.background.default,
    justifyContent: 'space-between',
    color: theme.colors.gray.a400,
    padding: '0.45rem 1.25rem 0.35rem',
    border: `solid ${theme.palette.grey[500]}`,
    borderRadius: '3.04px 3.04px 0 0',
    borderWidth: '0.06rem 0.06rem 0',
  },
  input: {
    display: 'none',
  },
  error: {
    paddingBlock: '0.75rem',
  },
  bottomBorder: {
    borderBottomWidth: '0.06rem',
  },
}))
